'use client';

/**
 * Shared user-card used by:
 *  - SuggestedCuratorsBlock (empty state + home widget)
 *  - /profile/[username]/followers
 *  - /profile/[username]/following
 *
 * Redesigned for visual balance:
 *  - Avatar + name block at top, stat line separate
 *  - Bio clamped to 2 lines with proper spacing
 *  - Follow button sits in the top-right corner, always aligned
 *  - Equal-height cards via flex-column + flex-grow on bio region
 */

import Link from 'next/link';
import { useState } from 'react';
import { SuggestedUser, User } from '@/types';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/lib/toast';

type Props = {
  user: SuggestedUser | User;
  statLine?: string;
  onFollowed?: (username: string, nowFollowing: boolean) => void;
  showFollowButton?: boolean;
  /** 'card' = vertical card (default), 'row' = compact horizontal list row */
  layout?: 'card' | 'row';
};

function defaultStatLine(u: SuggestedUser | User): string {
  const parts: string[] = [];
  if (typeof u.followers_count === 'number') {
    parts.push(`${u.followers_count.toLocaleString()} follower${u.followers_count === 1 ? '' : 's'}`);
  }
  if ('prompt_count' in u && typeof u.prompt_count === 'number') {
    parts.push(`${u.prompt_count.toLocaleString()} prompt${u.prompt_count === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}

export default function UserCardRow({
  user,
  statLine,
  onFollowed,
  showFollowButton = true,
  layout = 'card',
}: Props) {
  const { user: viewer } = useAuth();
  const toast = useToast();
  const [isFollowing, setIsFollowing] = useState<boolean>(!!user.is_following);
  const [busy, setBusy] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);

  const isSelf = viewer?.id === user.id;
  const canShowButton = showFollowButton && !!viewer && !isSelf;
  const statText = statLine ?? defaultStatLine(user);

  const handleToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy || !viewer) return;
    const next = !isFollowing;
    setIsFollowing(next);
    setBusy(true);
    try {
      if (next) await api.users.follow(user.username);
      else await api.users.unfollow(user.username);
      onFollowed?.(user.username, next);
    } catch {
      setIsFollowing(!next);
      toast.error('Couldn\u2019t update follow');
    } finally {
      setBusy(false);
    }
  };

  const btnLabel = isFollowing ? (hovered ? 'Unfollow' : 'Following') : 'Follow';
  const btnIcon  = isFollowing ? (hovered ? 'person_remove' : 'check') : 'person_add';
  const btnClass = isFollowing
    ? 'ps-btn ps-btn-secondary ps-btn-sm'
    : 'ps-btn ps-btn-primary ps-btn-sm';

  /* ── Avatar ── */
  const avatar = user.avatar_url ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={user.avatar_url}
      alt={user.name}
      style={{
        width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flex: 'none',
        border: '2px solid var(--bg-page)',
        boxShadow: '0 0 0 1px var(--line)',
      }}
    />
  ) : (
    <div style={{
      width: 48, height: 48, borderRadius: '50%', flex: 'none',
      background: 'linear-gradient(135deg, var(--accent), oklch(0.55 0.2 280))',
      color: 'white', display: 'grid', placeItems: 'center',
      fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700,
      boxShadow: '0 0 0 1px var(--line)',
    }}>
      {user.name.slice(0, 1).toUpperCase()}
    </div>
  );

  /* ── Follow button ── */
  const followBtn = canShowButton ? (
    <button
      type="button"
      className={btnClass}
      onClick={handleToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={busy}
      aria-busy={busy || undefined}
      style={{ flex: 'none', whiteSpace: 'nowrap' }}
      aria-label={btnLabel}
    >
      <span className="material-symbols-outlined ps-icon">{btnIcon}</span>
      {btnLabel}
    </button>
  ) : null;

  /* ══ CARD layout (default — used in SuggestedCuratorsBlock grid) ══ */
  if (layout === 'card') {
    return (
      <div
        onMouseEnter={() => setCardHovered(true)}
        onMouseLeave={() => setCardHovered(false)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          background: 'var(--bg-raised)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-md)',
          transition: 'box-shadow 0.18s, border-color 0.18s, transform 0.18s',
          boxShadow: cardHovered ? '0 6px 24px rgba(0,0,0,0.13)' : '0 1px 4px rgba(0,0,0,0.06)',
          borderColor: cardHovered ? 'var(--accent)' : 'var(--line)',
          transform: cardHovered ? 'translateY(-2px)' : 'none',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Header row: avatar + name/handle + follow button */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Link href={`/profile/${user.username}`} className="no-underline" style={{ flex: 'none' }}>
            {avatar}
          </Link>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Link href={`/profile/${user.username}`} className="no-underline">
              <div style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 16,
                fontWeight: 500,
                letterSpacing: '-0.015em',
                color: 'var(--ink)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
              }}>
                {user.name}
              </div>
              <div style={{
                fontSize: 12,
                color: 'var(--ink-3)',
                marginTop: 3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                @{user.username}
              </div>
            </Link>
          </div>

          {followBtn}
        </div>

        {/* Stat pills */}
        {statText && (
          <div style={{
            display: 'flex',
            gap: 8,
            marginTop: 12,
            flexWrap: 'wrap',
          }}>
            {statText.split(' · ').map((part, i) => (
              <span key={i} style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--ink-3)',
                background: 'var(--bg-soft)',
                padding: '2px 8px',
                borderRadius: 20,
                letterSpacing: '0.01em',
              }}>
                {part}
              </span>
            ))}
          </div>
        )}

        {/* Bio */}
        {user.bio && (
          <p style={{
            fontSize: 12.5,
            color: 'var(--ink-2)',
            lineHeight: 1.55,
            marginTop: 10,
            marginBottom: 0,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            flex: 1,
          }}>
            {user.bio}
          </p>
        )}
      </div>
    );
  }

  /* ══ ROW layout — compact horizontal list (followers / following pages) ══ */
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        background: 'var(--bg-raised)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <Link
        href={`/profile/${user.username}`}
        className="no-underline"
        style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}
      >
        {avatar}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 600,
            color: 'var(--ink)', letterSpacing: '-0.01em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {user.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>
            @{user.username}
          </div>
          {statText && (
            <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 3 }}>
              {statText}
            </div>
          )}
          {user.bio && (
            <div style={{
              fontSize: 12.5, color: 'var(--ink-2)', marginTop: 5,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
            }}>
              {user.bio}
            </div>
          )}
        </div>
      </Link>

      {followBtn}
    </div>
  );
}
